import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { isEmpty, parseReference } from "@medplum/core";
import { Organization, Patient, PractitionerRole } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Anchor } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import * as Sentry from "@sentry/react";

import { Loading } from "@/components/Loading";
import { ORGANIZATION_ID } from "@/constants/local-storage";
import { SUPPORT_EMAIL } from "@/constants/support";
import { OrganizationsContext } from "@/contexts/organizations";
import { DevelopmentFeatureFlags, PracticeFeatureFlags } from "@/feature-flags";
import { getFeatures } from "@/feature-flags/utils/feature-flags";
import NotFound from "@/NotFound";
import { PractitionerPermissionFlags } from "@/permission/constants/practitioner-permissions";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";

interface OrganizationsProviderProps {
  children: React.ReactNode;
}

/* OrganizationsProvider is used to set the active organization for the current user.

LocalStorage is being used rather than React Context because in the future we may want to keep
the Organization selection saved in the user's browser.

If we don't use LocalStorage, the first Organization would always be selected,
and every page reload that didn't include the Organization ID in the URL would redirect to it
instead of the expected active Organization.
*/
export default function OrganizationsProvider({ children }: OrganizationsProviderProps) {
  const medplum = useMedplum();
  const { practiceId } = useParams() as { practiceId: string | undefined | null };
  // TODO: if the client has multiple organizations and login on both on different tabs, it may cause issues
  const [activeOrganizationId, setActiveOrganizationId] = useState<string | null>(
    localStorage.getItem(ORGANIZATION_ID),
  );
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [permissions, setPermissions] = useState<PractitionerPermissionFlags[]>([]);
  const [features, setFeatures] = useState<{
    practice: PracticeFeatureFlags[];
    development: DevelopmentFeatureFlags[];
  }>({ practice: [], development: [] });
  const [isLoading, setIsLoading] = useState(true);

  const [profile, profileId, isPractitioner, isPatient] = useMemo(() => {
    const profile = medplum.getProfile();
    const profileId = profile?.id;
    const isPractitioner = profileId && profile?.resourceType === "Practitioner";
    const isPatient = profileId && profile?.resourceType === "Patient";
    return [profile, profileId, isPractitioner, isPatient];
  }, [medplum]);

  const patientManagingOrganization = useMemo(() => {
    if (!isPatient) return null;
    const patient = profile as Patient;
    return patient.managingOrganization;
  }, [isPatient, profile]);

  const organizationIds = useMemo(() => {
    return organizations.map(({ id }) => id);
  }, [organizations]);

  const setPractitionerPermissions = useCallback(
    (practitionerRole?: PractitionerRole) => {
      if (!practitionerRole) return;
      const permissions = practitionerRole.code?.map((code) => code.coding?.[0]?.code) ?? [];
      if (!permissions || isEmpty(permissions)) return;
      setPermissions(permissions as PractitionerPermissionFlags[]);
    },
    [setPermissions],
  );

  const setFeatureFlags = useCallback(
    async (organization: Organization | undefined) => {
      if (!organization) {
        setFeatures({ practice: [], development: [] });
        return;
      }

      const features = getFeatures(organization);
      setFeatures(features);
    },
    [setFeatures],
  );

  const refreshOrganizationFeaturesFromMedplum = useCallback(
    async (organizationId: string) => {
      try {
        const fresh = await medplum.readResource("Organization", organizationId);
        setOrganizations((previous) => {
          const index = previous.findIndex((item) => item.id === organizationId);
          if (index === -1) return previous;
          const next = [...previous];
          next[index] = fresh;
          return next;
        });
        if (organizationId === activeOrganizationId) await setFeatureFlags(fresh);
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    [medplum, activeOrganizationId, setFeatureFlags],
  );

  useEffect(() => {
    if (!isLoading || !isPractitioner) return;

    (async () => {
      try {
        const { bundle: practitionerRolesBundle } = await medplum.searchResources(
          "PractitionerRole",
          {
            practitioner: joinReferenceString("Practitioner", profileId!),
            active: true,
          },
        );

        const practitionerRoles = getResourcesFromBundle(
          "PractitionerRole",
          practitionerRolesBundle,
        ).filter(Boolean) as PractitionerRole[];

        const organizations: Organization[] = [];

        const organizationIdsQuery = practitionerRoles
          .map(({ organization }) => parseReference(organization)[1])
          .join(",");

        if (organizationIdsQuery) {
          const { bundle: organizationsBundle } = await medplum.searchResources("Organization", {
            _id: organizationIdsQuery,
          });

          organizations.push(
            ...(getResourcesFromBundle("Organization", organizationsBundle).filter(
              Boolean,
            ) as Organization[]),
          );
        }

        setOrganizations(organizations);

        const organization =
          organizations.find(({ id }) => id === activeOrganizationId) || organizations?.[0];
        const organizationId = organization?.id;

        if (organizationId) {
          localStorage.setItem(ORGANIZATION_ID, organizationId);
          setActiveOrganizationId(organizationId);
          const activePractitionerRole = practitionerRoles.find(
            ({ organization }) => organization?.reference?.split("/")[1] === organizationId,
          );
          setPractitionerPermissions(activePractitionerRole);
          setFeatureFlags(organization);
        } else {
          localStorage.removeItem(ORGANIZATION_ID);
          setActiveOrganizationId(null);
          showPracticeNotFoundNotification();
        }
      } catch (error) {
        showSystemErrorNotification();
        Sentry.captureException(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    activeOrganizationId,
    isLoading,
    isPractitioner,
    profileId,
    medplum,
    setPractitionerPermissions,
    setFeatureFlags,
  ]);

  useEffect(() => {
    if (!isLoading || !isPatient) return;

    (async () => {
      try {
        if (patientManagingOrganization?.reference) {
          const organization = await medplum.readReference(patientManagingOrganization);
          setOrganizations([organization]);
          const organizationId = organization?.id;

          if (organizationId) {
            localStorage.setItem(ORGANIZATION_ID, organizationId);
            setActiveOrganizationId(organizationId);
            setFeatureFlags(organization);
          } else {
            localStorage.removeItem(ORGANIZATION_ID);
            setActiveOrganizationId(null);
            showPracticeNotFoundNotification();
          }
        }
      } catch (error) {
        showSystemErrorNotification();
        Sentry.captureException(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    medplum,
    isLoading,
    isPatient,
    patientManagingOrganization,
    activeOrganizationId,
    organizations,
    setFeatureFlags,
  ]);

  const value = useMemo(
    () => ({ organizations, permissions, features, refreshOrganizationFeaturesFromMedplum }),
    [organizations, permissions, features, refreshOrganizationFeaturesFromMedplum],
  );

  if (!isPractitioner && !isPatient) {
    // Unauthenticated users won't have a profile, so we can't check for an active organization
    // Removing this would block the login page from loading, showing "Not found" instead
    return children;
  }

  if (isLoading) {
    return <Loading />;
  }

  if (
    !activeOrganizationId ||
    (practiceId && !organizationIds.includes(practiceId)) ||
    practiceId === "null" // localStorage might return null, which will then be converted to a string
  ) {
    return <NotFound />;
  }

  return <OrganizationsContext.Provider value={value}>{children}</OrganizationsContext.Provider>;
}

function showPracticeNotFoundNotification(): string {
  notifications.clean(); // avoids duplicates
  return notifications.show({
    title: "Practice not found",
    message: (
      <>
        You're not associated with any practices. Please{" "}
        <Anchor fz="sm" c="burgundy.9" href={`mailto:${SUPPORT_EMAIL}`}>
          contact support
        </Anchor>
        .
      </>
    ),
    color: "red.9",
    autoClose: false,
  });
}
